<template>
  <div class="workflow-card" :class="{ 'is-owned': owned }" @click="$emit('select', workflow)">
    <span v-if="owned" class="owned-badge">✅ Owned</span>
    <div class="card-icon">{{ workflow.icon }}</div>
    <div class="card-body">
      <h3 class="card-title">{{ workflow.name }}</h3>
      <p class="card-desc">{{ workflow.description }}</p>
    </div>
    <div class="card-footer">
      <span class="card-steps">
        <span class="steps-icon">📝</span>
        {{ $t('workflows_page.steps', { n: workflow.steps_count }) }}
      </span>
      <span v-if="workflow.schedule" class="card-schedule">
        ⏰ {{ workflow.schedule }}
      </span>
      <span class="card-category">{{ workflow.category }}</span>

      <span class="card-lang" :class="'lang-' + (workflow.lang || 'en')">
        {{ (workflow.lang || 'en') === 'ko' ? '🇰🇷' : ((workflow.lang || 'en') === 'multi' ? '🌐' : '🇺🇸') }}
      </span>
    </div>
    <div class="card-stats">
      <span class="stat-item" :title="$t('workflows_page.views_label')">
        👁️ {{ formatCount(workflow.views || 0) }}
      </span>
      <span class="stat-item" :title="$t('workflows_page.downloads_label')">
        📥 {{ formatCount(workflow.downloads || 0) }}
      </span>
      <span v-if="workflow.author" class="card-author">👤 {{ workflow.author }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WorkflowCard',
  props: {
    workflow: {
      type: Object,
      required: true,
    },
    owned: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['select'],
  methods: {
    formatCount(n) {
      if (n >= 1000) return (n / 1000).toFixed(1) + 'k'
      return n
    },
  },
}
</script>

<style scoped>
.workflow-card {
  background: var(--card-bg);
  border: 1px solid var(--surface2);
  border-radius: var(--radius);
  padding: 28px;
  cursor: pointer;
  transition: all var(--transition);
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  overflow: hidden;
}

.workflow-card.is-owned {
  border-color: var(--green);
  opacity: 0.7;
}

.workflow-card.is-owned::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  background: repeating-linear-gradient(
    -45deg,
    transparent,
    transparent 8px,
    rgba(112, 192, 128, 0.08) 8px,
    rgba(112, 192, 128, 0.08) 10px
  );
  z-index: 1;
}

.owned-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 11px;
  color: var(--green);
  background: rgba(112, 192, 128, 0.15);
  padding: 3px 8px;
  border-radius: 10px;
  font-weight: 600;
}

.workflow-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--blue), var(--mauve));
  opacity: 0;
  transition: opacity var(--transition);
}

.workflow-card:hover {
  transform: translateY(-4px);
  border-color: var(--blue);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35), 0 0 30px var(--accent-glow);
}

.workflow-card:hover::before {
  opacity: 1;
}

.card-icon {
  font-size: 36px;
  line-height: 1;
}

.card-body {
  flex: 1;
}

.card-title {
  font-size: 17px;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 8px;
}

.card-desc {
  font-size: 13px;
  color: var(--subtext0);
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.card-steps {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--subtext0);
  background: var(--mantle);
  padding: 4px 10px;
  border-radius: 6px;
}

.steps-icon {
  font-size: 12px;
}

.card-schedule {
  font-size: 12px;
  color: var(--green);
  background: rgba(112, 192, 128, 0.1);
  padding: 4px 10px;
  border-radius: 6px;
}

.card-category {
  font-size: 11px;
  color: var(--lavender);
  background: rgba(144, 160, 208, 0.1);
  padding: 4px 10px;
  border-radius: 6px;
  margin-left: auto;
}

.card-stats {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-top: 8px;
  border-top: 1px solid var(--surface1);
}

.stat-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--overlay1);
  cursor: default;
}

.card-lang {
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 6px;
}

.card-author {
  font-size: 11px;
  color: var(--overlay1);
  background: rgba(127, 132, 156, 0.1);
  padding: 3px 8px;
  border-radius: 6px;
  margin-left: auto;
}


</style>
